import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Copy, KeyRound, LogIn, Unplug } from 'lucide-react';
import { toast } from 'sonner';
import { GOOGLE_WORKSPACE_SCOPES, formatDateTime, messages, type GoogleWorkspaceStatus } from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';

const SETUP_STEPS = [
  'Google Cloud コンソールでプロジェクトを作り、「API とサービス → ライブラリ」で Gmail API を有効にする',
  '「OAuth 同意画面」でユーザーの種類を「内部」にして作成する（組織内のアカウントだけが接続でき、Google の審査は要りません）',
  '「認証情報 → 認証情報を作成 → OAuth クライアント ID」で種類「ウェブ アプリケーション」を選び、上の「リダイレクト URI」を「承認済みのリダイレクト URI」に登録する',
  '発行されたクライアント ID とクライアントシークレットをこの画面に貼り付けて保存する',
  '「Google で接続」を押し、メーラーで使う受信箱（例：info@自社ドメイン）でログインして許可する',
];

/** S79 Google Workspace（admin のみ）。メーラーで使う受信箱を OAuth で接続する */
export const SettingsGoogleWorkspacePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [hostedDomain, setHostedDomain] = useState('');
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: qk.googleWorkspace,
    queryFn: () => api.get<SingleResponse<GoogleWorkspaceStatus>>('/settings/google-workspace'),
  });
  const status = data?.data;

  useEffect(() => {
    if (!status) return;
    setClientId(status.clientId);
    setHostedDomain(status.hostedDomain);
  }, [status]);

  // Google の同意画面から戻ってきたときの結果を知らせる
  useEffect(() => {
    const result = searchParams.get('result');
    if (!result) return;
    if (result === 'connected') {
      toast.success('Google Workspace を接続しました', { description: searchParams.get('email') ?? undefined });
    } else {
      toast.error('Google Workspace に接続できませんでした', {
        description: searchParams.get('message') ?? undefined,
        duration: 15_000,
      });
    }
    setSearchParams({}, { replace: true });
    void queryClient.invalidateQueries({ queryKey: ['settings'] });
    void queryClient.invalidateQueries({ queryKey: ['mailer'] });
  }, [searchParams, setSearchParams, queryClient]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings'] });
    void queryClient.invalidateQueries({ queryKey: ['mailer'] });
  };

  const clientChanged =
    !!status &&
    (clientId.trim() !== status.clientId || hostedDomain.trim().toLowerCase() !== status.hostedDomain || !!clientSecret.trim());

  const save = useMutation({
    mutationFn: () =>
      api.put('/settings/integrations/google_workspace', {
        config: {
          clientId: clientId.trim(),
          hostedDomain: hostedDomain.trim(),
          ...(clientSecret.trim() ? { clientSecret: clientSecret.trim() } : {}),
        },
      }),
    onSuccess: () => {
      toast.success(messages.common.saved);
      setClientSecret('');
      invalidate();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const connect = useMutation({
    mutationFn: () => api.post<SingleResponse<{ url: string }>>('/settings/google-workspace/authorize'),
    // 同意画面へ移る。戻り先はこの画面
    onSuccess: (result) => window.location.assign(result.data.url),
    onError: (error) => toast.error(errorMessage(error)),
  });

  const disconnect = useMutation({
    mutationFn: () => api.post('/settings/google-workspace/disconnect'),
    onSuccess: () => {
      toast.success('接続を解除しました');
      setDisconnectOpen(false);
      invalidate();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const test = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<{ ok: boolean; message: string }>>('/settings/integrations/google_workspace/test'),
    onSuccess: (result) => {
      if (result.data.ok) toast.success(messages.settings.testSucceeded, { description: result.data.message });
      else toast.error(messages.settings.testFailed, { description: result.data.message });
      invalidate();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const copyRedirect = async () => {
    if (!status) return;
    try {
      await navigator.clipboard.writeText(status.redirectUri);
      toast.success('リダイレクト URI をコピーしました');
    } catch {
      toast.error('コピーできませんでした。手で選択してコピーしてください');
    }
  };

  if (isLoading || !status) return <SkeletonRows rows={5} />;

  const canConnect = !!status.clientId && status.hasClientSecret && !clientChanged;

  return (
    <>
      <PageHeader
        back="/settings"
        title={messages.settings.googleWorkspace}
        description="メーラーで使う受信箱を OAuth で接続します。パスワードは預からず、Google の同意画面で許可したアクセス権だけを使います。"
      />

      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {status.connected ? <CheckCircle2 aria-hidden className="size-5" /> : <Unplug aria-hidden className="size-5" />}
              接続状況
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {status.connected ? (
              <p className="text-body">
                <span className="font-semibold">{status.connectedEmail}</span>
                {status.connectedName && <span className="text-foreground-muted">（{status.connectedName}）</span>}
                を接続しています
                {status.connectedAt && (
                  <span className="block text-note text-foreground-muted">
                    接続日時 <span className="tabular">{formatDateTime(status.connectedAt)}</span>
                  </span>
                )}
              </p>
            ) : (
              <p className="text-body text-foreground-muted">
                未接続です。クライアント ID とシークレットを保存してから「Google で接続」を押してください。
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => connect.mutate()}
                disabled={!canConnect || connect.isPending}
                disabledReason={
                  clientChanged
                    ? '先に変更を保存してください'
                    : !canConnect
                      ? 'クライアント ID とシークレットを保存してください'
                      : undefined
                }
              >
                <LogIn aria-hidden />
                {status.connected ? '別のアカウントで接続し直す' : 'Google で接続'}
              </Button>
              {status.connected && (
                <>
                  <Button variant="outline" onClick={() => test.mutate()} disabled={test.isPending}>
                    {messages.settings.testConnection}
                  </Button>
                  <Button variant="ghost" onClick={() => navigate('/mail')}>
                    メーラーを開く
                  </Button>
                  <Button variant="outline" onClick={() => setDisconnectOpen(true)}>
                    接続を解除
                  </Button>
                </>
              )}
            </div>

            {status.lastTestedAt && (
              <p className="text-note text-foreground-muted">
                {messages.settings.lastTestedAt} <span className="tabular">{formatDateTime(status.lastTestedAt)}</span>
                {status.lastTestResult === 'success' ? '（成功）' : '（失敗）'}
                {status.lastTestMessage && <>：{status.lastTestMessage}</>}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound aria-hidden className="size-5" />
              OAuth クライアント
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field
              label="リダイレクト URI"
              hint="Google Cloud の OAuth クライアントの「承認済みのリダイレクト URI」にこの値をそのまま登録してください"
            >
              {(props) => (
                <div className="flex gap-2">
                  <Input {...props} readOnly value={status.redirectUri} className="font-mono text-note" />
                  <Button variant="outline" size="icon" onClick={() => void copyRedirect()} aria-label="コピー">
                    <Copy aria-hidden />
                  </Button>
                </div>
              )}
            </Field>

            <Field label="クライアント ID" required>
              {(props) => (
                <Input
                  {...props}
                  autoComplete="off"
                  spellCheck={false}
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="123456789012-xxxxxxxx.apps.googleusercontent.com"
                  className="font-mono"
                />
              )}
            </Field>

            <Field
              label="クライアントシークレット"
              required={!status.hasClientSecret}
              hint={
                status.hasClientSecret
                  ? '登録済みです（表示はしません）。差し替える場合だけ入力してください'
                  : messages.settings.keyMasked
              }
            >
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  autoComplete="off"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder={status.hasClientSecret ? '••••••••' : 'GOCSPX-...'}
                />
              )}
            </Field>

            <Field
              label="接続を許可するドメイン"
              hint="指定すると、このドメインの Google Workspace アカウントだけ接続できます（個人の Gmail を誤って接続するのを防ぎます）"
            >
              {(props) => (
                <Input
                  {...props}
                  value={hostedDomain}
                  onChange={(e) => setHostedDomain(e.target.value)}
                  placeholder="example.co.jp"
                />
              )}
            </Field>

            {status.connected && clientChanged && (
              <p className="rounded-control border-2 border-foreground p-3 text-note font-semibold">
                クライアントや許可ドメインを変えて保存すると、今の接続は解除されます。保存後にもう一度「Google で接続」してください。
              </p>
            )}

            <Button onClick={() => save.mutate()} disabled={save.isPending || !clientId.trim()}>
              {messages.common.save}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Google Cloud での準備</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ol className="list-decimal space-y-1 pl-5 text-body">
              {SETUP_STEPS.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <p className="text-note text-foreground-muted">
              求めるアクセス権：{GOOGLE_WORKSPACE_SCOPES.join('、')}（メールの閲覧・ラベル変更・送信。完全削除はできません）
            </p>
            <p className="text-note text-foreground-muted">
              Google Workspace で「サードパーティ製アプリのアクセス」を制限している場合は、管理コンソール → セキュリティ → API の制御 で、このクライアント ID を「信頼できる」に設定してください。
            </p>
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        title="接続を解除しますか"
        summary="メーラーが使えなくなります。Google 側のアクセス許可も取り消します。クライアント ID などの設定は残ります。"
        confirmText="接続を解除"
        onConfirm={() => disconnect.mutate()}
        isPending={disconnect.isPending}
      />
    </>
  );
};
